import { useTranslation } from "react-i18next";

import { colors } from "#app/theme/v2/colors";
import { radius } from "#app/theme/v2/radius";
import { spacing } from "#app/theme/v2/spacing";
import { IconButton } from "#components/atoms/IconButton/IconButton";
import { Text } from "#components/atoms/Typographies/Text";
import { Material } from "#modules/materials/infra/fetchCustomMaterials.api";
import { Icon } from "#shared/icons/Icon";
import { Flex } from "#shared/view/components/Flex";
import {
  MATERIAL_LIST_MAX_ROWS,
  MaterialStructureImportIssue,
  MaterialStructureImportResult,
} from "shared";

const MAX_VISIBLE_ISSUES = 5;

const ISSUE_LABEL_KEYS = {
  missing_type: "onboardingV2.materialImport.issues.missing_type",
  missing_name: "onboardingV2.materialImport.issues.missing_name",
  missing_grade: "onboardingV2.materialImport.issues.missing_grade",
  missing_length: "onboardingV2.materialImport.issues.missing_length",
  missing_density: "onboardingV2.materialImport.issues.missing_density",
  missing_price: "onboardingV2.materialImport.issues.missing_price",
  missing_width: "onboardingV2.materialImport.issues.missing_dimension",
  missing_height: "onboardingV2.materialImport.issues.missing_dimension",
  missing_diameter: "onboardingV2.materialImport.issues.missing_dimension",
  missing_outer_diameter: "onboardingV2.materialImport.issues.missing_dimension",
  missing_inner_diameter: "onboardingV2.materialImport.issues.missing_dimension",
  missing_thickness: "onboardingV2.materialImport.issues.missing_dimension",
  low_confidence_type: "onboardingV2.materialImport.issues.low_confidence_type",
  low_confidence_grade: "onboardingV2.materialImport.issues.low_confidence_grade",
  low_confidence_price_unit: "onboardingV2.materialImport.issues.low_confidence_price_unit",
  implausible_density: "onboardingV2.materialImport.issues.implausible_density",
  non_positive_price: "onboardingV2.materialImport.issues.non_positive_price",
  inner_diameter_not_below_outer:
    "onboardingV2.materialImport.issues.inner_diameter_not_below_outer",
  tube_thickness_exceeds_section:
    "onboardingV2.materialImport.issues.tube_thickness_exceeds_section",
} as const;

const FIELD_LABEL_KEYS = {
  name: "onboardingV2.materialImport.fields.name",
  grade: "onboardingV2.materialImport.fields.grade",
  type: "onboardingV2.materialImport.fields.type",
  dimensions: "onboardingV2.materialImport.fields.dimensions",
  price: "onboardingV2.materialImport.fields.price",
} as const;

const isKnownKey = <TKeys extends Record<string, string>>(
  keys: TKeys,
  code: string,
): code is Extract<keyof TKeys, string> => code in keys;

type MaterialImportAlertProps = {
  result: MaterialStructureImportResult;
  materials: Material[];
  onClose: () => void;
};

export const MaterialImportAlert = ({ result, materials, onClose }: MaterialImportAlertProps) => {
  const { t } = useTranslation();
  const isError = result.outcome === "failed";
  const skippedRows = isError ? [] : result.skipped;
  const issues = isError ? result.errors : [...result.skipped, ...result.warnings];
  const materialNameById = new Map(materials.map((material) => [material.id, material.name]));

  const formatIssue = (issue: MaterialStructureImportIssue) => {
    const location = issue.sheet
      ? t("onboardingV2.materialImport.location.sheet", { sheet: issue.sheet, row: issue.row })
      : t("onboardingV2.materialImport.location.row", { row: issue.row });
    const materialName =
      (issue.materialId ? materialNameById.get(issue.materialId) : undefined) ?? issue.label;
    const skippedTag = skippedRows.includes(issue)
      ? t("onboardingV2.materialImport.skippedTag")
      : undefined;
    const reasons = issue.codes
      .map((code) =>
        t(
          isKnownKey(ISSUE_LABEL_KEYS, code)
            ? ISSUE_LABEL_KEYS[code]
            : "onboardingV2.materialImport.issues.unknown",
        ),
      )
      .join(", ");

    return [skippedTag, location, materialName].filter(Boolean).join(" · ") + ` : ${reasons}`;
  };

  const description = isError
    ? t(`onboardingV2.materialImport.failure.${result.failureCode}`, {
        count: result.errors.length,
        maxRows: MATERIAL_LIST_MAX_ROWS,
        fields: result.missingFields
          .map((field) =>
            isKnownKey(FIELD_LABEL_KEYS, field) ? t(FIELD_LABEL_KEYS[field]) : field,
          )
          .join(", "),
      })
    : [
        result.skipped.length > 0 &&
          t("onboardingV2.materialImport.warnings.skipped", { count: result.skipped.length }),
        result.warnings.length > 0 &&
          t("onboardingV2.materialImport.warnings.description", { count: result.warnings.length }),
        result.priceAlerts.length > 0 &&
          t("onboardingV2.materialImport.warnings.priceAlerts", {
            count: result.priceAlerts.length,
          }),
      ]
        .filter(Boolean)
        .join(" ");

  const statusColors = isError ? colors.bg.status.error : colors.bg.status.warning;
  const borderColor = isError ? colors.border.error.secondary : colors.border.warning.secondary;

  return (
    <Flex
      role="alert"
      gap={spacing[3]}
      align="flex-start"
      style={{
        padding: spacing[4],
        borderRadius: radius.md,
        backgroundColor: statusColors.primary,
        border: `1px solid ${borderColor}`,
        flexShrink: 0,
        marginBottom: spacing[4],
      }}
    >
      <Icon
        name="alertTriangle"
        size={20}
        color={isError ? colors.icon.errorDefault : colors.icon.warning}
      />
      <Flex direction="column" gap={spacing[1]} style={{ flex: 1 }}>
        <Text size="base" type="medium">
          {isError
            ? t("onboardingV2.materialImport.failure.title")
            : t("onboardingV2.materialImport.warnings.title", { count: result.importedCount })}
        </Text>
        <Text size="small" color={colors.text.secondaryDefault}>
          {description}
        </Text>
        {issues.length > 0 && (
          <ul style={{ margin: 0, paddingLeft: spacing[4] }}>
            {issues.slice(0, MAX_VISIBLE_ISSUES).map((issue) => (
              <li key={`${issue.sheet || ""}-${issue.row}`}>
                <Text size="small">{formatIssue(issue)}</Text>
              </li>
            ))}
            {issues.length > MAX_VISIBLE_ISSUES && (
              <li>
                <Text size="small" color={colors.text.secondaryDefault}>
                  {t("onboardingV2.materialImport.moreIssues", {
                    count: issues.length - MAX_VISIBLE_ISSUES,
                  })}
                </Text>
              </li>
            )}
          </ul>
        )}
      </Flex>
      <IconButton aria-label={t("common.close")} variant="tertiary" size="sm" onClick={onClose}>
        <Icon name="x" size={16} />
      </IconButton>
    </Flex>
  );
};
